// Shared page for Store Rent / Salary / General Payment.
// Renders the list + an embedded multi-row entry form. The three pages
// (StoreRentPaymentPage, SalaryPaymentPage, GeneralPaymentPage) configure
// which extra columns appear (store_location, rent_period, month,
// manpower_category, expense_head) but otherwise share all logic.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Plus, Trash2, Search, X, Eye, Save, Building2, Download, Upload, FileSpreadsheet, FileDown } from "lucide-react";
import { StatusBadge } from "@/components/ui-helpers";
import { PendingApprovalBadge, TakeActionButton, pendingDocIds } from "@/components/PendingApprovalBadge";
import { useApprovalPendingMap } from "@/hooks/useApprovalPendingMap";
import { AttachmentsField } from "@/components/AttachmentsField";
import { ExportExcelButton } from "@/components/ExportExcelButton";
import { useBranch } from "@/contexts/BranchContext";
import { BranchFilter } from "@/components/BranchFilter";
import { PaginationControls } from "@/components/PaginationControls";
import { DateRangeFilter, inRange } from "@/components/DateRangeFilter";
import { useVoucherFormOpen, discardWithConfirm } from "@/lib/voucherGuard";
import { VoucherNumberInput } from "@/components/VoucherNumberInput";
import { RefreshMastersButton } from "@/components/RefreshMastersButton";
import { useMastersRefresh } from "@/hooks/useMastersRefresh";
import { useTabReturnRefresh } from "@/hooks/useTabReturnRefresh";
import { ReportingPeriodInput, ApprovedReportingPeriod } from "@/components/ReportingPeriodField";
import { SearchableSelect } from "@/components/SearchableSelect";

function emptyLine() {
  return {
    vendor_id: "", vendor_name: "",
    project_id: "", project_name: "",
    store_location: "", rent_period: "",
    month: "", manpower_category: "",
    expense_head: "",
    reporting_period: "",
    ref_no: "",
    amount: 0, gst_amount: 0, tds_amount: 0,
    remarks: "", attachment_url: "", attachment_name: "",
  };
}

function formatDate(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return d; }
}

export default function VendorBillPage({ moduleKey, moduleName, title, route, columns }) {
  const { active: activeBranch, branches } = useBranch();
  const mastersRefreshKey = useMastersRefresh();
  const [list, setList] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(true);   // form auto-open on first arrival
  const [formKey, setFormKey] = useState(0);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [filterBranch, setFilterBranch] = useState("");
  const [createdRange, setCreatedRange] = useState({ from: "", to: "" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(200);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = (p = page, size = pageSize) => {
    setLoading(true);
    return api.get(`/${route}`, { params: { skip: (p - 1) * size, limit: size } })
      .then(r => {
        setList(r.data || []);
        setTotal(parseInt(r.headers?.["x-total-count"] || "0", 10) || (r.data || []).length);
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => { setPage(1); load(1, pageSize); }, [route]); // eslint-disable-line
  useEffect(() => { load(page, pageSize); }, [page, pageSize]); // eslint-disable-line
  // Jun-2026 — silent list refresh when the user returns to this tab
  // (TabContent keeps pages mounted, so effects don't re-fire on tab switch).
  const tabRootRef = useTabReturnRefresh(() => load(page, pageSize));
  useEffect(() => {
    api.get("/vendors/picker").then(r => setVendors(r.data || []));
    api.get("/projects-picker").then(r => setProjects(r.data || []));
  }, [mastersRefreshKey]);

  // moduleKey on this shared page is "store-rent" / "salary" / "general-payment".
  // The backend's approval matrix uses snake_case keys.
  const approvalModule = moduleKey === "store-rent" ? "store_rent"
                       : moduleKey === "salary" ? "salary_payment"
                       : "general_payment";
  const pendingIds = useMemo(() => pendingDocIds(list), [list]);
  const pendingMap = useApprovalPendingMap(approvalModule, pendingIds);

  const filtered = list.filter(v => {
    if (filterBranch && v.branch_id !== filterBranch) return false;
    if (filter !== "ALL" && v.status !== filter) return false;
    if (!inRange(v.created_at || v.voucher_date, createdRange)) return false;
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    const topMatch = [v.voucher_number, v.status, v.branch_name, String(v.grand_total || "")]
      .some(s => (s || "").toString().toLowerCase().includes(q));
    if (topMatch) return true;
    return (v.lines || []).some(l => [
      l.vendor_name, l.vendor_code, l.project_name, l.ref_no, l.remarks,
      l.store_location, l.rent_period, l.month, l.manpower_category, l.expense_head,
    ].some(s => (s || "").toString().toLowerCase().includes(q)));
  });

  async function handleDelete(id) {
    if (!window.confirm("Delete this voucher?")) return;
    try { await api.delete(`/${route}/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  }

  async function downloadVoucherPdf(v) {
    try {
      const r = await api.get(`/${route}/${v.id}/voucher-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${moduleKey}-voucher-${(v.voucher_number || v.id).replace(/[\\/]/g, "-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Voucher PDF downloaded");
    } catch (e) {
      let detail = "Download failed";
      if (e?.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text())?.detail || detail; } catch { /* default */ }
      } else if (e?.response?.data?.detail) {
        detail = e.response.data.detail;
      }
      toast.error(detail);
    }
  }

  return (
    <div ref={tabRootRef} className="px-3 sm:px-5 py-5 max-w-full">
      <Toaster richColors />
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h1 className="font-display text-3xl font-bold" data-testid={`${moduleKey}-title`}>{title}</h1>
        <div className="flex items-center gap-2">
          {!open && (
            <>
              <ExportExcelButton
                endpoint={moduleKey === "store-rent" ? "/exports/store-rent-payments-excel"
                  : moduleKey === "salary" ? "/exports/salary-payments-excel"
                  : "/exports/general-payments-excel"}
                filename={`${moduleKey}-payments.xlsx`}
                label="Export Excel"
                testid={`${moduleKey}-export`}/>
              <button data-testid={`add-${moduleKey}-btn`}
                onClick={() => { setEditing(null); setOpen(true); }}
                className="inline-flex items-center gap-2 bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider">
                <Plus size={14} /> New {title}
              </button>
            </>
          )}
          {open && (
            <>
              <button data-testid={`view-${moduleKey}-list-btn`}
                onClick={() => setOpen(false)}
                className="inline-flex items-center gap-2 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 px-4 py-2 text-xs font-bold uppercase tracking-wider">
                View List
              </button>
              <RefreshMastersButton testid={`refresh-${moduleKey}-masters-btn`} />
            </>
          )}
        </div>
      </div>

      {open ? (
        <VendorBillForm
          key={formKey}
          moduleKey={moduleKey}
          moduleName={moduleName}
          title={title}
          route={route}
          columns={columns}
          editing={editing}
          vendors={vendors}
          projects={projects}
          activeBranch={activeBranch}
          branches={branches}
          onClose={() => { setOpen(false); setEditing(null); }}
          onSaved={() => {
            // Refresh the list in the background, clear edit state, and remount the
            // form for a fresh new voucher so the user can keep entering vouchers.
            load();
            setEditing(null);
            setFormKey(k => k + 1);
          }}
        />
      ) : (
        <>
          <div className="flex gap-1 mb-4 border-b border-slate-200 overflow-x-auto">
            {[["ALL","All"],["Draft","Draft"],["Pending","Pending"],["Approved","Approved"],["Rejected","Rejected"]].map(([k,l]) => (
              <button key={k} data-testid={`tab-${moduleKey}-${k}`} onClick={() => setFilter(k)}
                className={`px-4 py-2.5 text-xs uppercase tracking-wider font-bold border-b-2 -mb-px whitespace-nowrap ${
                  filter === k ? "border-[#F97316] text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"
                }`}>{l}</button>
            ))}
          </div>

          <div className="flex items-center gap-3 mb-3">
            <div className="relative flex-1 max-w-md">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input data-testid={`${moduleKey}-search`} type="text" value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search voucher, vendor, project, ref, location, period, head…"
                className="w-full pl-9 pr-8 py-2 text-xs border border-slate-300 focus:border-[#F97316] focus:outline-none bg-white"/>
              {search && <button onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X size={13}/></button>}
            </div>
            <BranchFilter value={filterBranch} onChange={setFilterBranch} testid={`${moduleKey}-filter-branch`} />
            <DateRangeFilter value={createdRange} onChange={setCreatedRange} testidPrefix={moduleKey} label="Created" />
            <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">
              {filtered.length} {filter === "ALL" ? "shown" : filter}{total > list.length ? ` · ${total} total` : ""}
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="border border-slate-200 bg-slate-50 p-10 text-center text-sm text-slate-500">
              No {title.toLowerCase()} vouchers yet. Click &quot;New {title}&quot; to create one.
            </div>
          ) : (
            <div className="border border-slate-200 bg-white overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b-2 border-slate-200">
                  <tr>{["Voucher No", "Date", "Branch", "Lines", "Total Amount", "GST", "TDS", "Net Payable", "Status", "Actions"].map(h =>
                    <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-3 whitespace-nowrap">{h}</th>
                  )}</tr>
                </thead>
                <tbody>
                  {filtered.map(v => (
                    <tr key={v.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                      <td className="py-3 px-3 font-bold text-slate-900">{v.voucher_number}</td>
                      <td className="py-3 px-3 text-slate-600">{formatDate(v.voucher_date)}</td>
                      <td className="py-3 px-3">{v.branch_name ? <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 px-1.5 py-0.5 font-bold uppercase tracking-wider text-[10px]"><Building2 size={10}/> {v.branch_name}</span> : "—"}</td>
                      <td className="py-3 px-3 text-slate-700">{(v.lines || []).length}</td>
                      <td className="py-3 px-3 text-right tabular">{formatINR(v.total_amount)}</td>
                      <td className="py-3 px-3 text-right tabular">{formatINR(v.total_gst)}</td>
                      <td className="py-3 px-3 text-right tabular text-rose-600">{formatINR(v.total_tds)}</td>
                      <td className="py-3 px-3 text-right tabular font-bold">{formatINR(v.grand_total)}</td>
                      <td className="py-3 px-3"><PendingApprovalBadge status={v.status} pendingInfo={pendingMap[v.id]} /></td>
                      <td className="py-3 px-3 whitespace-nowrap">
                        {pendingMap[v.id] && (
                          <span className="mr-2 inline-block align-middle">
                            <TakeActionButton pendingInfo={pendingMap[v.id]} testid={`take-action-${v.id}`} />
                          </span>
                        )}
                        <button data-testid={`view-${moduleKey}-${v.id}`}
                          onClick={() => { setEditing(v); setOpen(true); }}
                          className="text-slate-500 hover:text-[#F97316] inline-flex items-center gap-1 mr-3" title="View / Edit">
                          <Eye size={14} />
                        </button>
                        <button data-testid={`pdf-${moduleKey}-${v.id}`}
                          onClick={() => downloadVoucherPdf(v)}
                          className="text-red-600 hover:text-red-700 inline-flex items-center gap-1 mr-3" title="Download Voucher PDF">
                          <FileDown size={14} />
                        </button>
                        {v.status !== "Approved" && (
                          <button data-testid={`delete-${moduleKey}-${v.id}`}
                            onClick={() => handleDelete(v.id)} className="text-red-600 hover:underline">Delete</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <PaginationControls page={page} pageSize={pageSize} total={total} loading={loading}
            onPage={setPage} onPageSize={(n) => { setPage(1); setPageSize(n); }}
            pageSizeOptions={[50, 100, 200, 500]} />
        </>
      )}
    </div>
  );
}

function VendorBillForm({ moduleKey, moduleName, title, route, columns, editing, vendors, projects, activeBranch, branches, onClose, onSaved }) {
  const navigate = useNavigate();
  useVoucherFormOpen();
  // Salary Payment is for internal members only → no GST applicable and no
  // Invoice/Ref No required (internal voucher uses voucher number itself).
  const isSalary = moduleKey === "salary";
  // Discard exits to the application dashboard (not the list).
  const handleDiscard = () => discardWithConfirm(() => navigate("/"), `this ${title.toLowerCase()}`);

  const [form, setForm] = useState(() => editing || {
    voucher_date: new Date().toISOString().slice(0, 10),
    branch_id: activeBranch?.id || "",
    branch_name: activeBranch?.branch_name || activeBranch?.name || "",
    notes: "",
    attachments: [],
    lines: [emptyLine()],
  });
  // Branch-scoped libraries: filter dropdowns strictly by selected branch
  const bvendors = vendors.filter(v => !form.branch_id || v.branch_id === form.branch_id);
  const bprojects = projects.filter(p => !form.branch_id || p.branch_id === form.branch_id);
  const [defaultProjectId, setDefaultProjectId] = useState(
    editing?.lines?.[0]?.project_id || ""
  );
  const [saving, setSaving] = useState(false);
  const [suggestions, setSuggestions] = useState({}); // field → []
  const [bulkUploading, setBulkUploading] = useState(false);

  // No effect: branch is initialized in useState from activeBranch and the user
  // can override via the select. If activeBranch resolves after mount and form
  // was created with an empty branch_id, the user must pick from the dropdown.

  // Load autocomplete suggestions for each module-specific column
  useEffect(() => {
    const fields = columns.map(c => c.field).filter(f =>
      ["store_location", "rent_period", "month", "manpower_category", "expense_head", "ref_no"].includes(f)
    );
    Promise.all(fields.map(f =>
      api.get(`/${route}/_suggestions/${f}`).then(r => [f, r.data || []]).catch(() => [f, []])
    )).then(pairs => setSuggestions(Object.fromEntries(pairs)));
  }, [route, columns]);

  const totals = useMemo(() => {
    const t = (form.lines || []).reduce((acc, l) => {
      acc.amount += Number(l.amount || 0);
      acc.gst += Number(l.gst_amount || 0);
      acc.tds += Number(l.tds_amount || 0);
      return acc;
    }, { amount: 0, gst: 0, tds: 0 });
    t.net = t.amount + t.gst - t.tds;
    return t;
  }, [form.lines]);

  function setLine(i, patch) {
    setForm(f => ({ ...f, lines: f.lines.map((l, idx) => idx === i ? { ...l, ...patch } : l) }));
  }
  // Auto-fill TDS from vendor.tds_rate when vendor or amount changes. The user
  // can override the value — we set `tds_user_edited=true` once they touch
  // the TDS cell so subsequent vendor/amount tweaks won't clobber their input.
  // Vendor identity is by Vendor Code/ID ONLY (2026-Jul-16 — two vendors may
  // share a name; nine same-name pairs got merged onto one bank account in
  // batch BATCH-0057). The code dropdown resolves by vendor.id; the name is
  // auto-filled and never used for lookup.
  function patchVendor(i, vendorId) {
    const v = vendors.find(x => x.id === vendorId);
    setForm(f => ({
      ...f, lines: f.lines.map((l, idx) => {
        if (idx !== i) return l;
        const next = { ...l, vendor_id: v?.id || "", vendor_code: v?.vendor_code || "", vendor_name: v?.name || "" };
        if (v && !l.tds_user_edited) {
          const rate = Number(v.tds_rate || 0);
          next.tds_amount = +(Number(l.amount || 0) * rate / 100).toFixed(2);
          next.tds_rate_applied = rate;
        }
        return next;
      }),
    }));
  }
  function patchAmount(i, amount) {
    setForm(f => ({
      ...f, lines: f.lines.map((l, idx) => {
        if (idx !== i) return l;
        const next = { ...l, amount };
        if (!l.tds_user_edited && l.vendor_id) {
          const ven = vendors.find(x => x.id === l.vendor_id);
          const rate = Number(ven?.tds_rate || 0);
          next.tds_amount = +(Number(amount || 0) * rate / 100).toFixed(2);
          next.tds_rate_applied = rate;
        }
        return next;
      }),
    }));
  }
  function addLine() {
    const baseLine = emptyLine();
    if (defaultProjectId) {
      const p = projects.find(x => x.id === defaultProjectId);
      baseLine.project_id = defaultProjectId;
      baseLine.project_name = p?.project_name || p?.name || "";
    }
    setForm(f => ({ ...f, lines: [...f.lines, baseLine] }));
  }
  function removeLine(i) {
    setForm(f => ({ ...f, lines: f.lines.length === 1 ? [emptyLine()] : f.lines.filter((_, idx) => idx !== i) }));
  }

  // When the default project changes, apply it to any rows that don't have one yet
  function applyDefaultProject(pid) {
    setDefaultProjectId(pid);
    const p = projects.find(x => x.id === pid);
    setForm(f => ({
      ...f,
      lines: f.lines.map(l => (!l.project_id || !l.project_name)
        ? { ...l, project_id: pid, project_name: p?.project_name || p?.name || "" }
        : l),
    }));
  }

  async function downloadTemplate() {
    try {
      const resp = await api.get(`/vendor-bills/${moduleName}/template`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `${moduleName}_template.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not download template"); }
  }

  async function handleBulkUpload(file) {
    setBulkUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const resp = await api.post(`/vendor-bills/${moduleName}/parse-excel`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { lines: parsed, errors, imported, skipped } = resp.data;
      if (!parsed || parsed.length === 0) {
        toast.error(`No valid rows found. ${(errors || []).join(" / ")}`);
        return;
      }
      // Merge into form lines — apply default project to rows missing project
      const merged = parsed.map(l => {
        if (!l.project_id && defaultProjectId) {
          const p = projects.find(x => x.id === defaultProjectId);
          return { ...emptyLine(), ...l, project_id: defaultProjectId, project_name: l.project_name || p?.project_name || p?.name || "" };
        }
        // Bulk parser now resolves project_id server-side, so this is
        // a defensive fallback only. Compare against `project_name`
        // (the actual field on the /api/projects payload) — the older
        // `p.name` compare always returned undefined and left every
        // row orphan-projected (2026-Feb-06 fix).
        const matchP = projects.find(p => ((p.project_name || p.name) || "").toLowerCase() === (l.project_name || "").toLowerCase());
        // VENDOR: trust the backend's Vendor-Code resolution VERBATIM.
        // (2026-Jul-16 fix — the old name-based re-match here overwrote the
        // correct vendor_id with the FIRST same-name vendor's id, merging
        // distinct vendors' payments onto one bank account.)
        return { ...emptyLine(), ...l,
          project_id: l.project_id || matchP?.id || "",
          project_name: l.project_name || matchP?.project_name || matchP?.name || "",
        };
      });
      setForm(f => ({ ...f, lines: merged }));
      toast.success(`${imported} row(s) imported${skipped ? `, ${skipped} skipped` : ""}. Review and click Save.`);
      if (errors && errors.length) {
        errors.slice(0, 3).forEach(e => toast.warning(e));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBulkUploading(false);
    }
  }

  async function save(e) {
    e?.preventDefault?.();
    // Trim empty rows (rows with no vendor)
    const lines = (form.lines || []).filter(l => l.vendor_id || (l.vendor_name || "").trim() || (l.vendor_code || "").trim());
    if (lines.length === 0) { toast.error("Add at least one line with a vendor"); return; }
    for (let i = 0; i < lines.length; i++) {
      if (!lines[i].vendor_id) { toast.error(`Line ${i + 1}: select the vendor by Vendor Code — name alone is not accepted (multiple vendors can share a name)`); return; }
      if (!(Number(lines[i].amount) > 0)) { toast.error(`Line ${i + 1}: Amount must be > 0`); return; }
      if (!isSalary && !(lines[i].ref_no || "").trim()) { toast.error(`Line ${i + 1}: Invoice / Ref No is mandatory.`); return; }
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        lines,
        branch_id: form.branch_id || activeBranch?.id || "",
        branch_name: form.branch_name || activeBranch?.name || "",
      };
      if (editing?.id) await api.put(`/${route}/${editing.id}`, payload);
      else await api.post(`/${route}`, payload);
      toast.success(`${title} saved — auto-submitted for approval`);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  return (
    <form onSubmit={save} className="bg-white border border-slate-200 p-5 space-y-5" data-testid={`${moduleKey}-form`}>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <label className="block">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-bold">
            Voucher No
            <span className="ml-2 text-slate-400 normal-case font-normal">(auto · admin can override)</span>
          </div>
          <VoucherNumberInput
            module={moduleName}
            value={form.voucher_number || ""}
            onChange={v => setForm({ ...form, voucher_number: v })}
            testidPrefix={`${moduleKey}-voucher-number`}
          />
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-bold">Voucher Date *</div>
          <input data-testid={`${moduleKey}-date`} type="date" required value={form.voucher_date}
            onChange={e => setForm({ ...form, voucher_date: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 text-sm focus:border-[#F97316] focus:outline-none"/>
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-bold">Branch *</div>
          <select data-testid={`${moduleKey}-branch`} value={form.branch_id || activeBranch?.id || ""} required
            onChange={e => {
              const b = branches.find(x => x.id === e.target.value);
              setForm({ ...form, branch_id: e.target.value, branch_name: b?.branch_name || b?.name || "" });
            }}
            className="w-full px-3 py-2 border border-slate-300 text-sm focus:border-[#F97316] focus:outline-none">
            <option value="">Select branch…</option>
            {(branches || []).map(b => <option key={b.id} value={b.id}>{b.branch_name || b.name}</option>)}
          </select>
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-bold flex items-center justify-between">
            <span>Default Project</span>
            <span className="text-[9px] normal-case text-slate-400 font-normal">applies to all rows</span>
          </div>
          <select data-testid={`${moduleKey}-default-project`} value={defaultProjectId}
            onChange={e => applyDefaultProject(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 text-sm focus:border-[#F97316] focus:outline-none">
            <option value="">—</option>
            {(bprojects || []).map(p => <option key={p.id} value={p.id}>{p.project_name || p.name}</option>)}
          </select>
        </label>
      </div>

      {/* Voucher Remark — full-width textarea so it gets the visual weight it
       * deserves. This field travels to the document's `notes` column and is
       * surfaced verbatim to the approver inside the Approval Inbox → View
       * modal (DocSummary picks `notes` first in its priority chain). The
       * old single-line "Notes (Header)" input was easy to miss; user
       * requested 2026-06-20 that this be a proper free-text remark field
       * on Store Rent / Salary / General Payment vouchers.                */}
      <label className="block mt-3" data-testid={`${moduleKey}-voucher-remark-section`}>
        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-bold flex items-center justify-between">
          <span>Voucher Remark</span>
          <span className="text-[9px] normal-case text-slate-400 font-normal">visible to approver in approval inbox</span>
        </div>
        <textarea data-testid={`${moduleKey}-notes`}
          value={form.notes || ""}
          rows={2}
          onChange={e => setForm({ ...form, notes: e.target.value })}
          placeholder="Free-text remark covering the entire voucher (purpose, context, special instructions for the approver, etc.)"
          className="w-full px-3 py-2 border border-slate-300 text-sm focus:border-[#F97316] focus:outline-none resize-y leading-relaxed"/>
      </label>

      {/* Supporting documents — visible to approvers during approval */}
      <div className="border border-slate-200 bg-slate-50/50 px-4 py-3" data-testid={`${moduleKey}-attachments-section`}>
        <AttachmentsField value={form.attachments || []}
          onChange={atts => setForm({ ...form, attachments: atts })}
          label="Supporting Documents"/>
      </div>

      {/* Excel template + bulk upload */}
      <div className="flex flex-wrap items-center gap-3 bg-slate-50 border border-slate-200 px-4 py-3">
        <FileSpreadsheet size={16} className="text-emerald-700"/>
        <div className="text-xs text-slate-700 font-bold uppercase tracking-wider">Bulk Upload</div>
        <button type="button" onClick={downloadTemplate} data-testid={`${moduleKey}-template-btn`}
          className="inline-flex items-center gap-1 text-xs border border-slate-300 px-3 py-1.5 hover:border-[#F97316] hover:text-[#F97316]">
          <Download size={12}/> Download Excel Template
        </button>
        <label className="inline-flex items-center gap-1 text-xs bg-emerald-600 text-white px-3 py-1.5 font-bold uppercase tracking-wider cursor-pointer hover:bg-emerald-700">
          <Upload size={12}/> {bulkUploading ? "Uploading…" : "Upload Filled Excel"}
          <input type="file" data-testid={`${moduleKey}-upload-input`} className="hidden"
            accept=".xlsx,.xls" disabled={bulkUploading}
            onChange={e => { if (e.target.files?.[0]) { handleBulkUpload(e.target.files[0]); e.target.value = ""; } }}/>
        </label>
        <span className="text-[10px] text-slate-500 ml-auto">Imported rows REPLACE current rows. Save afterwards to submit.</span>
      </div>

      {/* Row-based entry table */}
      <div className="border border-slate-200 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left py-2 px-1.5 text-[9px] uppercase font-bold text-slate-500 w-8">#</th>
              <th className="text-left py-2 px-1.5 text-[9px] uppercase font-bold text-slate-500 min-w-[130px]">Vendor Code *</th>
              <th className="text-left py-2 px-1.5 text-[9px] uppercase font-bold text-slate-500 min-w-[150px]">Vendor Name
                <div className="text-[8px] normal-case font-normal text-slate-400 leading-tight">auto from code</div>
              </th>
              <th className="text-left py-2 px-1.5 text-[9px] uppercase font-bold text-slate-500 min-w-[130px]">Project</th>
              {columns.map(c =>
                <th key={c.field} className="text-left py-2 px-1.5 text-[9px] uppercase font-bold text-slate-500 min-w-[110px]">
                  {c.label}{c.field === "ref_no" ? " *" : ""}
                  {c.type === "month" && <div className="text-[8px] normal-case font-normal text-slate-400 leading-tight">Management reporting only — not the accounting/GST date</div>}
                </th>
              )}
              <th className="text-right py-2 px-1.5 text-[9px] uppercase font-bold text-slate-500 min-w-[100px] w-[100px]">Amount *</th>
              {!isSalary && (
                <th className="text-right py-2 px-1.5 text-[9px] uppercase font-bold text-slate-500 min-w-[90px] w-[90px]">GST</th>
              )}
              <th className="text-right py-2 px-1.5 text-[9px] uppercase font-bold text-slate-500 min-w-[100px] w-[100px]">TDS</th>
              <th className="text-left py-2 px-1.5 text-[9px] uppercase font-bold text-slate-500 min-w-[120px]">Remarks</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {form.lines.map((l, i) => (
              <tr key={i} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/40">
                <td className="py-1.5 px-1.5 text-slate-400 tabular align-top pt-2.5">{i + 1}</td>
                <td className="py-1.5 px-1.5 align-top">
                  <SearchableSelect
                    testid={`${moduleKey}-line-${i}-vendor-code`}
                    value={l.vendor_id || ""}
                    onChange={(id) => patchVendor(i, id)}
                    items={bvendors}
                    getLabel={(v) => v.vendor_code || "(no code)"}
                    getSubLabel={(v) => v.name}
                    getSearchText={(v) => `${v.vendor_code || ""} ${v.name || ""}`}
                    placeholder="Code…"
                    searchPlaceholder="Search code or name…"
                    inputClassName="px-1.5 py-1"
                  />
                  {!l.vendor_id && (l.vendor_name || "").trim() && (
                    <div className="text-[9px] text-rose-600 mt-0.5" data-testid={`${moduleKey}-line-${i}-vendor-unresolved`}>
                      Select a code — name alone not accepted
                    </div>
                  )}
                </td>
                <td className="py-1.5 px-1.5 align-top">
                  <input value={l.vendor_name || ""} readOnly tabIndex={-1}
                    data-testid={`${moduleKey}-line-${i}-vendor`}
                    placeholder="Auto from code"
                    className="w-full px-1.5 py-1 border border-slate-200 bg-slate-50 text-slate-700 text-xs focus:outline-none cursor-default"/>
                </td>
                <td className="py-1.5 px-1.5 align-top">
                  <input list={`projects-list`} value={l.project_name}
                    onChange={e => {
                      const p = projects.find(x => (x.project_name || x.name) === e.target.value);
                      setLine(i, { project_name: e.target.value, project_id: p?.id || "" });
                    }}
                    placeholder="Type or pick…"
                    className="w-full px-1.5 py-1 border border-slate-300 text-xs focus:border-[#F97316] focus:outline-none"/>
                </td>
                {columns.map(c => {
                  const dlid = `${moduleKey}-${c.field}-list`;
                  const isRefNo = c.field === "ref_no";
                  if (c.type === "month") {
                    // Phase C-Lite — line-level Project Reporting Period (GP only).
                    const rpApproved = editing?.status === "Approved";
                    return (
                      <td key={c.field} className="py-2 px-2 align-top">
                        {rpApproved ? (
                          <ApprovedReportingPeriod
                            resource={route}
                            docId={editing.id}
                            lineIndex={i}
                            value={l[c.field] || ""}
                            onSaved={p => setLine(i, { [c.field]: p })}
                            voucherLabel={`${title} ${editing.voucher_number || ""} · Line ${i + 1}`}
                            testid={`${moduleKey}-line-${i}-${c.field}`}
                            className="w-full px-1.5 py-1 border border-slate-300 text-xs"
                            showNote={false}
                          />
                        ) : (
                          <ReportingPeriodInput
                            value={l[c.field] || ""}
                            onChange={v => setLine(i, { [c.field]: v })}
                            testid={`${moduleKey}-line-${i}-${c.field}`}
                            className="w-full px-1.5 py-1 border border-slate-300 text-xs focus:border-[#F97316] focus:outline-none"
                            showNote={false}
                          />
                        )}
                      </td>
                    );
                  }
                  return (
                    <td key={c.field} className="py-2 px-2 align-top">
                      <input list={dlid} value={l[c.field] || ""}
                        onChange={e => setLine(i, { [c.field]: e.target.value })}
                        placeholder={c.placeholder || ""}
                        required={isRefNo}
                        data-testid={`${moduleKey}-line-${i}-${c.field}`}
                        className={`w-full px-1.5 py-1 border text-xs focus:border-[#F97316] focus:outline-none ${isRefNo && !(l[c.field] || "").trim() ? "border-rose-300 bg-rose-50/40" : "border-slate-300"}`}/>
                    </td>
                  );
                })}
                <td className="py-1.5 px-1.5 align-top">
                  <input type="number" step="any" min="0" value={l.amount || ""}
                    onChange={e => patchAmount(i, e.target.value === "" ? 0 : Number(e.target.value))}
                    data-testid={`${moduleKey}-line-${i}-amount`}
                    className="w-full px-1.5 py-1 border border-slate-300 text-xs text-right focus:border-[#F97316] focus:outline-none"/>
                </td>
                {!isSalary && (
                  <td className="py-1.5 px-1.5 align-top">
                    <input type="number" step="any" min="0" value={l.gst_amount || ""}
                      onChange={e => setLine(i, { gst_amount: e.target.value === "" ? 0 : Number(e.target.value) })}
                      data-testid={`${moduleKey}-line-${i}-gst_amount`}
                      className="w-full px-1.5 py-1 border border-slate-300 text-xs text-right focus:border-[#F97316] focus:outline-none"/>
                  </td>
                )}
                <td className="py-1.5 px-1.5 align-top">
                  <input type="number" step="any" min="0" value={l.tds_amount || ""}
                    onChange={e => setLine(i, { tds_amount: e.target.value === "" ? 0 : Number(e.target.value), tds_user_edited: true })}
                    data-testid={`${moduleKey}-line-${i}-tds_amount`}
                    className="w-full px-1.5 py-1 border border-slate-300 text-xs text-right focus:border-[#F97316] focus:outline-none"/>
                  {l.tds_rate_applied > 0 && !l.tds_user_edited && (
                    <div className="text-[9px] text-emerald-700 mt-0.5 tabular">@ {l.tds_rate_applied}% (auto)</div>
                  )}
                  {l.tds_user_edited && (
                    <div className="text-[9px] text-slate-500 mt-0.5">manual override</div>
                  )}
                </td>
                <td className="py-1.5 px-1.5 align-top">
                  <input value={l.remarks}
                    onChange={e => setLine(i, { remarks: e.target.value })}
                    placeholder="—"
                    className="w-full px-1.5 py-1 border border-slate-300 text-xs focus:border-[#F97316] focus:outline-none"/>
                </td>
                <td className="py-1.5 px-1.5 align-top">
                  <button type="button" onClick={() => removeLine(i)}
                    data-testid={`${moduleKey}-remove-line-${i}`}
                    className="text-rose-600 hover:text-rose-800 p-1" title="Remove row">
                    <Trash2 size={13}/>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between gap-3">
        <button type="button" onClick={addLine} data-testid={`${moduleKey}-add-line`}
          className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider border border-slate-300 px-3 py-1.5 hover:border-[#F97316] hover:text-[#F97316]">
          <Plus size={12}/> Add Row
        </button>
        <div className="flex items-center gap-6 text-xs">
          <div><span className="text-slate-500 mr-1">Amount</span><span className="font-bold tabular">{formatINR(totals.amount)}</span></div>
          {!isSalary && (
            <div><span className="text-slate-500 mr-1">+ GST</span><span className="font-bold tabular">{formatINR(totals.gst)}</span></div>
          )}
          <div><span className="text-slate-500 mr-1">− TDS</span><span className="font-bold tabular text-rose-600">{formatINR(totals.tds)}</span></div>
          <div className="text-sm"><span className="text-slate-500 mr-1">Net Payable</span><span className="font-bold tabular text-emerald-700" data-testid={`${moduleKey}-net-payable`}>{formatINR(totals.net)}</span></div>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
        <button type="button" onClick={handleDiscard} data-testid={`discard-${moduleKey}-btn`}
          className="px-5 py-2.5 bg-white border border-slate-300 text-slate-700 text-sm font-bold uppercase tracking-wider hover:bg-slate-50 hover:border-rose-400 hover:text-rose-700">
          Discard
        </button>
        <button type="submit" disabled={saving} data-testid={`save-${moduleKey}-btn`}
          className="px-5 py-2.5 bg-[#F97316] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#EA580C] disabled:opacity-60 inline-flex items-center gap-2">
          <Save size={14}/> {saving ? "Saving…" : `Save ${title}`}
        </button>
      </div>

      {/* Shared datalists for project + module-specific autocomplete fields
        * (vendor datalist removed 2026-Jul-16 — vendor is picked by CODE via
        * SearchableSelect; name-based picking merged same-name vendors). */}
      <datalist id="projects-list">
        {(bprojects || []).map(p => <option key={p.id} value={p.project_name || p.name} />)}
      </datalist>
      {columns.map(c => (
        <datalist key={c.field} id={`${moduleKey}-${c.field}-list`}>
          {(suggestions[c.field] || []).map(s => <option key={s} value={s} />)}
        </datalist>
      ))}
    </form>
  );
}
